import { useState, useEffect, useCallback, useRef } from 'react';
import lib from '../../../assets/utils/lib';
import { useWebSocket } from '../../../assets/utils/useWebSocket';

const POLL_MS = 60_000;

/**
 * Poll pending staff-upload batch count for Organizations badge.
 * Also refreshes on focus and when WebSocket `staff_upload_updated` fires
 * with `action: submitted` (see HR_STAFF_UPLOAD_WEBSOCKET_GUIDE).
 */
export function usePendingStaffUploadsCount(enabled = true) {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const enabledRef = useRef(enabled);

  enabledRef.current = enabled;

  const refresh = useCallback(async () => {
    if (!enabledRef.current) {
      setCount(0);
      return 0;
    }
    setLoading(true);
    try {
      const total = await lib.getPendingStaffUploadsCount();
      setCount(total);
      return total;
    } catch (_) {
      return 0;
    } finally {
      setLoading(false);
    }
  }, []);

  const handleWsMessage = useCallback((message) => {
    const eventType = message?.event || message?.type;
    if (eventType !== 'staff_upload_updated') return;
    const payload = message?.payload || message || {};
    const action = String(payload.action || '').toLowerCase();
    // Refresh on any lifecycle update; submitted is the badge-critical case
    if (!action || action === 'submitted' || action === 'approved' || action === 'rejected' || action === 'failed' || action === 'partially_reviewed') {
      refresh();
    }
  }, [refresh]);

  useWebSocket('corporations', {
    enabled: Boolean(enabled),
    onMessage: handleWsMessage,
    autoReconnect: true,
  });

  useEffect(() => {
    if (!enabled) {
      setCount(0);
      return undefined;
    }

    refresh();
    const timer = setInterval(refresh, POLL_MS);

    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);

    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [enabled, refresh]);

  return { count, loading, refresh };
}

export default usePendingStaffUploadsCount;
